import {
  AnalysisBounds,
  incidentsTransactionCategories,
  Transaction,
  BankStatement,
  TransactionCategoryFilters,
  TransactionTypeFilters,
  AccountItem,
} from '@algoan/dashboard/model';
import { sortTransactionsByDateAndId } from '../../formatter/transactions-formatter';
import { filterOutUndefined, monitoring } from '@algoan/dashboard/utils';
import { transactionCategoryToCashFlowNature, transactionTypeToCashFlowNature } from '@algoan/dashboard/converters';

export const formatBankStatements = (
  accounts: AccountItem[],
  transactions: Transaction[],
  analysisBounds?: AnalysisBounds,
): BankStatement[] => {
  return transactions
    .slice()
    .sort(sortTransactionsByDateAndId)
    .reduce<BankStatement[]>((bankStatements, transaction) => {
      const account = accounts.find((account) => account.id === transaction.accountId);

      if (!account) {
        monitoring.logError({
          fingerprint: 'orphan_transaction',
          message: 'No account found for the transaction',
          context: {
            accountIds: accounts.map((receivedAccount) => receivedAccount.id),
            accountLength: accounts.length,
            transactionId: transaction.id,
            transactionAccountId: transaction.accountId,
            level: 'fatal',
          },
        });

        return bankStatements;
      }

      return bankStatements.concat({
        ...transaction,
        accountName: account.name,
        accountType: account.type,
        accountIndex: account.index,
        accountColor: account.color,
      });
    }, []);
};

export const formatCashFlowCategoriesFilters = (transactions: Transaction[]): TransactionCategoryFilters => {
  const uniqueCashFlowsTypes = filterOutUndefined(
    transactions
      .map((transaction) => transaction.enrichments?.category)
      .filter((transaction, index, transactions) => transactions.indexOf(transaction) === index),
  );

  return {
    incomes: uniqueCashFlowsTypes.filter(
      (cashFlowType) =>
        (transactionCategoryToCashFlowNature[cashFlowType] === 'incomes' ||
          transactionCategoryToCashFlowNature[cashFlowType] === 'resources') &&
        !incidentsTransactionCategories.includes(cashFlowType),
    ),
    expenses: uniqueCashFlowsTypes.filter(
      (cashFlowType) =>
        (transactionCategoryToCashFlowNature[cashFlowType] === 'expenses' ||
          transactionCategoryToCashFlowNature[cashFlowType] === 'fixedCharges' ||
          transactionCategoryToCashFlowNature[cashFlowType] === 'loans') &&
        !incidentsTransactionCategories.includes(cashFlowType),
    ),
    incidents: uniqueCashFlowsTypes.filter((cashFlowType) => incidentsTransactionCategories.includes(cashFlowType)),
  };
};

export const formatCashFlowTypeFilters = (transactions: Transaction[]): TransactionTypeFilters => {
  const uniqueCashFlowsTypes = filterOutUndefined(
    transactions
      .map((transaction) => transaction.enrichments?.type)
      .filter((transaction, index, transactions) => transactions.indexOf(transaction) === index),
  );

  return {
    incomes: uniqueCashFlowsTypes.filter(
      (cashFlowType) =>
        transactionTypeToCashFlowNature[cashFlowType] === 'incomes' ||
        transactionTypeToCashFlowNature[cashFlowType] === 'resources',
    ),
    expenses: uniqueCashFlowsTypes.filter(
      (cashFlowType) =>
        transactionTypeToCashFlowNature[cashFlowType] === 'expenses' ||
        transactionTypeToCashFlowNature[cashFlowType] === 'fixedCharges' ||
        transactionTypeToCashFlowNature[cashFlowType] === 'loans',
    ),
  };
};
